/**
 * App-level overlay that renders the active permission-prompt modal.
 * Handles the accept/dismiss logic per prompt type.
 *
 * Mount once at the app level (inside PromptQueueProvider + NotificationProvider).
 */

import React, { useCallback, useMemo } from 'react';
import { usePromptQueue } from '../../contexts/prompt-queue-context';
import { useNotifications } from '../../contexts/notification-context';
import { BiometricsStorage } from '../../helpers/biometrics-storage';
import { useBiometricSetup } from '../../hooks/use-biometric-setup';
import PermissionPromptModal from './permission-prompt-modal';
import { requestPushPermission, registerPush } from '../../platform/push';

/**
 * Detects if the user is on iOS.
 */
function isIos(): boolean {
  return /iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export const PromptQueueOverlay: React.FC = () => {
  const { activePrompt, handleAccept, handleDismiss } = usePromptQueue();
  const { registerPushSubscription } = useNotifications();
  const { handleSetup: setupBiometrics } = useBiometricSetup();

  // Check if push permission is already denied by the browser
  const pushBlocked = useMemo(() => {
    if (!activePrompt || activePrompt.id !== 'push-notifications') return false;
    return typeof Notification !== 'undefined' && Notification.permission === 'denied';
  }, [activePrompt]);

  const onPrimary = useCallback(async () => {
    if (!activePrompt) return;

    if (activePrompt.id === 'push-notifications') {
      try {
        const permission = await requestPushPermission();
        if (permission === 'granted') {
          await registerPush();
          handleAccept();
        } else {
          handleDismiss();
        }
      } catch {
        handleDismiss();
      }
      return;
    }

    if (activePrompt.id === 'biometrics') {
      try {
        const success = await setupBiometrics();
        if (success) {
          BiometricsStorage.setBiometricsEnabled(true);
          handleAccept();
        } else {
          handleDismiss();
        }
      } catch {
        handleDismiss();
      }
      return;
    }
  }, [activePrompt, handleAccept, handleDismiss, registerPushSubscription, setupBiometrics]);

  if (!activePrompt) return null;

  // When push notifications are blocked, override the modal text
  const title = pushBlocked ? 'Notifications are blocked.' : activePrompt.title;
  const subtitle = pushBlocked
    ? isIos()
      ? 'Open Settings \u2192 Notifications \u2192 Mailander and enable Allow Notifications.'
      : 'Open your device Settings and enable notifications for Mailander.'
    : undefined;
  const primaryLabel = pushBlocked ? 'OK' : activePrompt.primaryLabel;

  return (
    <PermissionPromptModal
      open={!!activePrompt}
      onOpenChange={(open) => { if (!open) handleDismiss(); }}
      title={title}
      {...(subtitle !== undefined ? { subtitle } : {})}
      primaryLabel={primaryLabel}
      onPrimary={onPrimary}
      onSkip={handleDismiss}
    />
  );
};
